import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, Mail, Phone, MapPin, Globe, AlertCircle, ArrowRight, User, Lock } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;1,400&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
  :root {
    --c: #FAF8F4; --c2: #F2EDE3; --c3: #E6DFD1; --c4: #D9CFBD;
    --forest: #1C3830; --fmid: #2B5244;
    --gold: #C09A40; --gold-bg: #F5EECB;
    --stone: #796F61; --ink: #18150F; --white: #FFFFFF;
    --red: #B83232; --red-bg: #FEF0EE;
  }
  .reg * { box-sizing: border-box; }
  .reg {
    font-family: 'DM Sans', sans-serif;
    min-height: 100vh;
    background: var(--c);
    display: flex; align-items: flex-start;
    justify-content: center;
    padding: 48px 16px 64px;
  }
  ::selection { background: rgba(192,154,64,.2); color: var(--forest); }

  /* Card */
  .reg-card {
    width: 100%; max-width: 780px;
    background: var(--white);
    border: 1px solid var(--c3);
    border-radius: 20px;
    box-shadow: 0 8px 40px rgba(28,56,48,.08), 0 2px 8px rgba(28,56,48,.04);
    overflow: hidden;
  }

  /* Header */
  .reg-header {
    background: var(--forest);
    padding: 44px 48px 40px;
    text-align: center;
    position: relative; overflow: hidden;
  }
  .reg-header::before {
    content: '';
    position: absolute; top: -50px; right: -50px;
    width: 200px; height: 200px; border-radius: 50%;
    background: rgba(192,154,64,.1);
  }
  .reg-header::after {
    content: '';
    position: absolute; bottom: -60px; left: -30px;
    width: 160px; height: 160px; border-radius: 50%;
    background: rgba(192,154,64,.07);
  }
  .reg-header-mark {
    width: 52px; height: 52px; border-radius: 14px;
    background: rgba(245,238,203,.15); border: 1.5px solid rgba(192,154,64,.3);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 18px; position: relative; z-index: 1;
  }
  .reg-header h1 {
    font-family: 'Lora', serif; font-weight: 600;
    font-size: 26px; color: var(--c);
    letter-spacing: -.025em; margin: 0 0 6px;
    position: relative; z-index: 1;
  }
  .reg-header p {
    font-size: 14px; color: rgba(250,248,244,.6);
    margin: 0; position: relative; z-index: 1;
  }

  /* Body */
  .reg-body { padding: 40px 48px; }
  @media (max-width: 600px) { .reg-body { padding: 28px 20px; } .reg-header { padding: 36px 20px 32px; } }

  /* Error alert */
  .reg-error {
    display: flex; align-items: flex-start; gap: 10px;
    background: var(--red-bg); border: 1px solid rgba(184,50,50,.2);
    border-radius: 10px; padding: 12px 14px;
    margin-bottom: 28px;
    font-size: 13.5px; color: var(--red); line-height: 1.45;
  }

  /* Section header */
  .reg-sec {
    display: flex; align-items: center; gap: 9px;
    margin: 0 0 20px;
  }
  .reg-sec-icon {
    width: 30px; height: 30px; border-radius: 8px;
    background: var(--gold-bg); border: 1px solid rgba(192,154,64,.25);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .reg-sec h3 {
    font-family: 'Lora', serif; font-size: 16px;
    font-weight: 600; color: var(--forest);
    letter-spacing: -.015em; margin: 0;
  }

  /* Divider */
  .reg-div { height: 1px; background: var(--c3); margin: 32px 0; }

  /* Grid */
  .reg-grid { display: grid; gap: 16px; grid-template-columns: 1fr; }
  @media (min-width: 560px) { .reg-grid { grid-template-columns: 1fr 1fr; } }

  /* Field */
  .reg-field { display: flex; flex-direction: column; gap: 6px; }
  .reg-label {
    font-size: 12.5px; font-weight: 600;
    color: var(--ink); letter-spacing: .01em;
  }
  .reg-label span { color: var(--gold); margin-left: 2px; }

  /* Input wrapper */
  .reg-input-wrap { position: relative; }
  .reg-input-icon {
    position: absolute; left: 12px; top: 50%;
    transform: translateY(-50%); pointer-events: none;
    color: var(--stone); opacity: .65;
  }
  .reg-input {
    width: 100%; height: 42px;
    border: 1.5px solid var(--c3);
    border-radius: 9px; background: var(--white);
    font-size: 13.5px; font-family: 'DM Sans', sans-serif;
    color: var(--ink); transition: border-color .14s, box-shadow .14s;
    outline: none; padding: 0 14px;
  }
  .reg-input.with-icon { padding-left: 38px; }
  .reg-input::placeholder { color: var(--stone); opacity: .55; }
  .reg-input:focus {
    border-color: var(--gold);
    box-shadow: 0 0 0 3px rgba(192,154,64,.12);
  }
  .reg-input:disabled { background: var(--c2); opacity: .6; cursor: not-allowed; }

  /* Select */
  .reg-select {
    width: 100%; height: 42px;
    border: 1.5px solid var(--c3);
    border-radius: 9px; background: var(--white);
    font-size: 13.5px; font-family: 'DM Sans', sans-serif;
    color: var(--ink); padding: 0 14px;
    outline: none; cursor: pointer;
    transition: border-color .14s, box-shadow .14s;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M2 4l4 4 4-4' stroke='%23796F61' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
  }
  .reg-select:focus {
    border-color: var(--gold);
    box-shadow: 0 0 0 3px rgba(192,154,64,.12);
  }

  /* Checkbox row */
  .reg-check-row {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 14px 16px; border-radius: 10px;
    background: var(--c2); border: 1px solid var(--c3);
    margin-top: 4px;
  }
  .reg-check-box {
    width: 17px; height: 17px; border-radius: 4px;
    border: 1.5px solid var(--c4); background: var(--white);
    flex-shrink: 0; cursor: pointer; margin-top: 1px;
    transition: all .14s; appearance: none;
    display: flex; align-items: center; justify-content: center;
  }
  .reg-check-box:checked {
    background: var(--gold); border-color: var(--gold);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'%3E%3Cpath d='M2 5l2.5 2.5 3.5-4' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: center;
  }
  .reg-check-label { font-size: 13px; color: var(--stone); line-height: 1.5; }
  .reg-check-label a { color: var(--gold); text-decoration: none; font-weight: 500; }
  .reg-check-label a:hover { text-decoration: underline; }

  /* Submit button */
  .reg-submit {
    width: 100%; height: 46px;
    background: var(--forest); color: var(--c);
    border: none; border-radius: 10px;
    font-size: 14.5px; font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer; transition: all .15s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 3px 12px rgba(28,56,48,.2); margin-top: 8px;
  }
  .reg-submit:hover:not(:disabled) {
    background: var(--fmid);
    box-shadow: 0 5px 18px rgba(28,56,48,.26);
    transform: translateY(-1px);
  }
  .reg-submit:disabled { opacity: .6; cursor: not-allowed; transform: none; }

  /* Spinner */
  @keyframes reg-spin { to { transform: rotate(360deg); } }
  .reg-spinner {
    width: 16px; height: 16px; border-radius: 50%;
    border: 2px solid rgba(250,248,244,.3);
    border-top-color: var(--c);
    animation: reg-spin .7s linear infinite;
  }

  /* Footer */
  .reg-footer {
    text-align: center;
    border-top: 1px solid var(--c3);
    padding: 20px 24px;
    font-size: 13px; color: var(--stone);
  }
  .reg-footer a { color: var(--gold); text-decoration: none; font-weight: 500; }
  .reg-footer a:hover { text-decoration: underline; }
`;

function RegStyles() {
  useEffect(() => {
    const id = 'reg-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = STYLES;
      document.head.appendChild(el);
    }
  }, []);
  return null;
}

// ─── Section heading ──────────────────────────────────────────────────────────
function SecHead({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  return (
    <div className="reg-sec">
      <div className="reg-sec-icon">
        <Icon size={15} style={{ color: 'var(--gold)', strokeWidth: 1.7 }} />
      </div>
      <h3>{label}</h3>
    </div>
  );
}

// ─── Field ────────────────────────────────────────────────────────────────────
function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="reg-field">
      <label className="reg-label">
        {label}{required && <span>*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function CompanyRegister() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '', registrationNumber: '',
    email: '', password: '', confirmPassword: '',
    contactName: '', contactTitle: '',
    phone: '', location: '', website: '',
    companySize: '', industry: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (!agreeTerms) { setError('You must agree to the terms and conditions'); return; }
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast({ title: 'Registration successful!', description: 'Your company has been registered. You can now log in.' });
      navigate('/company/login');
    } catch {
      setError('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <RegStyles />
      <div className="reg">
        <div className="reg-card">

          {/* Header */}
          <div className="reg-header">
            <div className="reg-header-mark">
              <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
                <rect x="2"  y="6"  width="4" height="8" rx="0.5" fill="#C09A40" opacity="0.9"/>
                <rect x="6"  y="3"  width="4" height="11" rx="0.5" fill="#F5EECB"/>
                <rect x="10" y="5"  width="4" height="9"  rx="0.5" fill="#C09A40" opacity="0.7"/>
                <rect x="7"  y="10" width="2" height="4"  rx="0.3" fill="#C09A40"/>
              </svg>
            </div>
            <h1>Register Your Company</h1>
            <p>Join Vett &amp; Venture's Problem Bank</p>
          </div>

          {/* Body */}
          <div className="reg-body">
            {error && (
              <div className="reg-error">
                <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* ── Company Information ── */}
              <SecHead icon={Building2} label="Company Information" />
              <div className="reg-grid">
                <Field label="Company Name" required>
                  <input className="reg-input" name="companyName" value={formData.companyName}
                    onChange={handleChange} placeholder="e.g., Kenya Breweries Ltd" required />
                </Field>
                <Field label="Registration Number">
                  <input className="reg-input" name="registrationNumber" value={formData.registrationNumber}
                    onChange={handleChange} placeholder="e.g., CBL/2010/1234" />
                </Field>
                <Field label="Industry" required>
                  <select className="reg-select" name="industry" value={formData.industry} onChange={handleChange} required>
                    <option value="">Select industry</option>
                    <option value="fmcg">FMCG</option>
                    <option value="agro_processing">Agro-processing</option>
                    <option value="automotive">Automotive</option>
                    <option value="logistics">Logistics</option>
                    <option value="pharmaceutical">Pharmaceutical</option>
                    <option value="textile">Textile</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Company Size">
                  <select className="reg-select" name="companySize" value={formData.companySize} onChange={handleChange}>
                    <option value="">Select size</option>
                    <option value="1-10">1–10 employees</option>
                    <option value="11-50">11–50 employees</option>
                    <option value="51-200">51–200 employees</option>
                    <option value="201-500">201–500 employees</option>
                    <option value="501+">501+ employees</option>
                  </select>
                </Field>
                <Field label="Location" required>
                  <div className="reg-input-wrap">
                    <MapPin size={14} className="reg-input-icon" />
                    <input className="reg-input with-icon" name="location" value={formData.location}
                      onChange={handleChange} placeholder="e.g., Nairobi, Kenya" required />
                  </div>
                </Field>
                <Field label="Website">
                  <div className="reg-input-wrap">
                    <Globe size={14} className="reg-input-icon" />
                    <input className="reg-input with-icon" name="website" value={formData.website}
                      onChange={handleChange} placeholder="https://www.company.com" />
                  </div>
                </Field>
              </div>

              <div className="reg-div" />

              {/* ── Contact Person ── */}
              <SecHead icon={User} label="Contact Person" />
              <div className="reg-grid">
                <Field label="Full Name" required>
                  <input className="reg-input" name="contactName" value={formData.contactName}
                    onChange={handleChange} placeholder="e.g., Jane Wanjiku" required />
                </Field>
                <Field label="Job Title" required>
                  <input className="reg-input" name="contactTitle" value={formData.contactTitle}
                    onChange={handleChange} placeholder="e.g., Plant Manager" required />
                </Field>
                <Field label="Phone Number" required>
                  <div className="reg-input-wrap">
                    <Phone size={14} className="reg-input-icon" />
                    <input className="reg-input with-icon" name="phone" value={formData.phone}
                      onChange={handleChange} placeholder="+254 722 123 456" required />
                  </div>
                </Field>
                <Field label="Email" required>
                  <div className="reg-input-wrap">
                    <Mail size={14} className="reg-input-icon" />
                    <input className="reg-input with-icon" type="email" name="email" value={formData.email}
                      onChange={handleChange} placeholder="jane@company.com" required />
                  </div>
                </Field>
              </div>

              <div className="reg-div" />

              {/* ── Account Security ── */}
              <SecHead icon={Lock} label="Account Security" />
              <div className="reg-grid">
                <Field label="Password" required>
                  <div className="reg-input-wrap">
                    <Lock size={14} className="reg-input-icon" />
                    <input className="reg-input with-icon" type="password" name="password"
                      value={formData.password} onChange={handleChange} placeholder="••••••••" required />
                  </div>
                </Field>
                <Field label="Confirm Password" required>
                  <div className="reg-input-wrap">
                    <Lock size={14} className="reg-input-icon" />
                    <input className="reg-input with-icon" type="password" name="confirmPassword"
                      value={formData.confirmPassword} onChange={handleChange} placeholder="••••••••" required />
                  </div>
                </Field>
              </div>

              {/* Terms */}
              <div className="reg-check-row" style={{ marginTop: 20 }}>
                <input
                  type="checkbox" id="terms" className="reg-check-box"
                  checked={agreeTerms}
                  onChange={e => setAgreeTerms(e.target.checked)}
                />
                <label htmlFor="terms" className="reg-check-label">
                  I agree to the{' '}
                  <Link to="/terms">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy">Privacy Policy</Link>
                </label>
              </div>

              <button type="submit" className="reg-submit" disabled={isLoading} style={{ marginTop: 24 }}>
                {isLoading
                  ? <><div className="reg-spinner" /> Registering…</>
                  : <>Register Company <ArrowRight size={16} /></>
                }
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="reg-footer">
            Already registered?{' '}
            <Link to="/company/login">Sign in here</Link>
          </div>
        </div>
      </div>
    </>
  );
}
